import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { distinctUntilChanged } from 'rxjs/operators';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { DrugScheduleEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { ILocationToExclude } from 'src/app/shared/interfaces/ILocationToExclude.interface';
import { OrderTypeEnum } from 'src/app/shared/interfaces/OrderTypeEnum';
import { IAutocompleteDataRow } from 'src/app/shared/interfaces/autocomplete-data-row.interface';
import { FieldType } from 'src/app/shared/interfaces/field-type.enum';
import { GenericTableColumn } from 'src/app/shared/interfaces/genetic-table-column';
import { IOptionItem } from 'src/app/shared/interfaces/option-item.interface';
import { RowType } from 'src/app/shared/interfaces/raw-type.enum';
import { ISaveAttachedFileEvent } from 'src/app/shared/interfaces/save-attached-file-event.interface';
import { ISelectedNdc } from 'src/app/shared/interfaces/selected-ndc.interface';
import { IViolatingTransactionDateNdc } from 'src/app/shared/interfaces/violating-transaction-date-ndc.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { FormControls } from 'src/app/shared/utilities/form-controls.util';
import { ICreateOrderSaleReturn } from '../../interfaces/create-order-sale-return.interface';
import { getFileTypes } from '../../interfaces/file-types-list';
import { ISellReturnOrderDialogSubmittedEvent } from '../../interfaces/sell-return-order-dialog-submitted-event.interface';
import { IInventoryModel } from 'src/app/shared/interfaces/IInventoryModel';
import { NUMBER_MIN_VALUE } from 'src/app/shared/constants/constants';
import Decimal from 'decimal.js';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { ISignedFileObject } from 'src/app/shared/components/file-uploader/signed-file-object.interface';


type ICreateOrderSaleReturnControl = FormControls<ICreateOrderSaleReturn>;

@UntilDestroy()
@Component({
  selector: 'app-sell-return-order-dialog',
  templateUrl: './sell-return-order-dialog.component.html',
  styleUrls: ['./sell-return-order-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [InventoryLocationsService, BackCountService]

})
export class SellReturnOrderDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dialogHeader;
  @Input() searchLabel = 'Which drug do you want to add?';
  @Input() countHeader = 'Sell';
  @Input() recordingSale: boolean = true;
  @Input() activityType: ActivityTypesEnum;

  @Output() submit: EventEmitter<ISellReturnOrderDialogSubmittedEvent> = new EventEmitter();
  @Output() cancel: EventEmitter<void> = new EventEmitter();

  addNdcMode: boolean;

  form: FormGroup;
  orderSalesReturnsFormArray: FormArray<FormGroup<ICreateOrderSaleReturnControl>> = new FormArray<FormGroup<ICreateOrderSaleReturnControl>>([]);

  columns: GenericTableColumn<ICreateOrderSaleReturn>[] = [];

  files: ISignedFileObject[] = [];

  fileTypes: IOptionItem[] = getFileTypes(this.recordingSale ? OrderTypeEnum.SaleToProvider : OrderTypeEnum.ReturnToWholesaler);

  @ViewChild('dialog') dialog;
  @ViewChild('po') po ;
  @ViewChild('mra') mra ;

  @ViewChild('supplierBuyerDetails') supplierBuyerDetails;
  @ViewChild('table') table;

  dialogId = "createOrder";

  /**
   *
   */
  constructor(
    private fb: FormBuilder,
     private store: Store, 
     private formattedDatePipe: FormattedDatePipe, 
     private transactionsService: TransactionsService, 
     private cdr: ChangeDetectorRef,
     private inventoryLocationsService: InventoryLocationsService,
     private backCountService: BackCountService
    ) {
    this.initForm();
  }
 
  ngOnInit(): void {
    this.addNdcMode = true;
    this.columns = [
      new GenericTableColumn<ICreateOrderSaleReturn>({ header: 'Drug', field: 'friendlyName', RowType: RowType.Double,
        doubleRowField: {
          headingField: 'friendlyName', 
          secondaryField1: 'packageNdc11Digits'
        },
        style: {width: '30%'}
      })];

      if (this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
        this.columns.push(
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: 'Location',
            field: 'locationID',
            RowType: RowType.Location,
            sortable: false,
            locationRow: {startingField: 'starting', onHandField: 'onHand', countField: 'count', drugIDField: 'drugID'},
            style: {width: '20%'}
          })
        );
      }
      this.columns = [
        ...this.columns,
        ...[
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: 'Starting',
            field: 'starting',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          }),
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: this.countHeader,
            field: 'count',
            RowType: RowType.InputNumber,
            sortable: false,
            style: { 'text-align': 'right', width: '9%' },
            class: '!pl-8 !pr-8'
          }),
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: 'On-Hand',
            field: 'onHand',
            fieldType: FieldType.Number,
            sortable: false,
            style: {width: '111px'}
          })
        ],
      ];

      const showBackCountHeaderPredicate = () => this.backCountService.isAnyRequiresBackCount();
      const showBackCountTogglePredicate = (rowValue) => this.backCountService.isDrugRequiresBackCount(rowValue.drugID);

      this.columns.push (new GenericTableColumn<ICreateOrderSaleReturn>({
        header: '',
        RowType: RowType.BackCountNotResolvedTooltip,
        additionalProps: {locationID: 'locationID', drugID: 'drugID'},
        sortable: false,
        style: {width: '0px'},
        class: '!pl-0 !pr-0',
        headerDisplayPredicate: showBackCountHeaderPredicate,
        rowValueDisplayPredicate: showBackCountTogglePredicate
      }));
      this.columns.push(new GenericTableColumn<ICreateOrderSaleReturn>({
        header: 'SOH',
        field: 'availableSOH',
        fieldType: FieldType.Number,
        sortable: false,
        style: {width: '71px'},
      }));
      this.columns.push ( new GenericTableColumn<ICreateOrderSaleReturn>({
        header: 'BACK-COUNT OK?',
        field: 'isBackCountMatches',
        RowType: RowType.BackCount,
        sortable: false,
        style: {width: '165px'},
        headerDisplayPredicate: showBackCountHeaderPredicate,
        rowValueDisplayPredicate: showBackCountTogglePredicate
        }),
      )
      

      this.columns = [
        ...this.columns,
        ...[
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: '',
            field: 'note',
            sortable: false,
            RowType: RowType.Note,
            noteRow: {noteDrugNameField: 'friendlyName'},
            class: '!pr-2 !pl-4',
            style: {width: '1rem'},
          }),
          new GenericTableColumn<ICreateOrderSaleReturn>({
            header: '',
            sortable: false,
            RowType: RowType.RemoveRowButton,
            style: {width: '1rem'},
          }),
        ],
      ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.recordingSale) {
      this.form.addControl('poNumber', new FormControl('', [Validators.required]));
    } else {
      this.form.addControl('mra', new FormControl('', [Validators.required]));
    }
  }

  optionSelected(ndc: IInventoryModel): void {
    if (!ndc) return;
    
    //exclude D/E
    let positiveInventoryNDCs = ndc?.positiveInventoryBalanceLocationIDs;
    if (this.activityType === ActivityTypesEnum.SoldToProvider) {
      positiveInventoryNDCs = positiveInventoryNDCs?.filter(locationId => locationId !== this.store.selectSnapshot(InventoryState.damagedExpiredLocationID));
    }
    this.inventoryLocationsService.addLocationsWithPositiveInventory(ndc.drugID, ndc?.positiveInventoryBalanceLocationIDs);
    const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(this.activityType, ndc.schedule, ndc.drugID);
    this.inventoryLocationsService.addLocationsWithPositiveInventory(ndc.drugID, positiveInventoryNDCs);
    const group = this.fb.group({
     drugID: ndc?.drugID,
     friendlyName: ndc?.friendlyName,
     schedule: ndc.schedule,
     packageNdc11Digits: ndc?.packageNdc11Digits,
     locationID: ndc?.locationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID),
     starting: ndc.onHand,
     count: ["", [Validators.required, Validators.max(ndc.onHand), Validators.min(NUMBER_MIN_VALUE)]],
     onHand: ndc.onHand,
     isBackCountMatches: [null, backCountEnabledForTheActivity ? Validators.required : null],
     note: null,
     gpi: ndc.gpi,
     similarOnHand: ndc.similarOnHand,
     availableSOH: ndc.similarOnHand
    }) as FormGroup<ICreateOrderSaleReturnControl>;

    

    group.get('count').valueChanges
    .pipe(untilDestroyed(this), distinctUntilChanged())
    .subscribe(x => {
      const starting = group.get('starting').value;
      group.get('onHand').setValue(new Decimal(+starting).sub(new Decimal(+x)).toNumber());
      this.adjustSOH(ndc.gpi);
    });
    
     // Change FormArray reference to trigger change detection in child
    const newFormArray = new FormArray([...this.orderSalesReturnsFormArray.controls, group]);
    this.orderSalesReturnsFormArray = newFormArray;
    this.addNdcMode = false;
    this.adjustSOH(ndc.gpi);
  }

  private adjustSOH(gpi: string): void {
     const rows = this.orderSalesReturnsFormArray
      .controls.filter(control => control.value.gpi === gpi);
      if (rows?.length) {
        const actualSOH = new Decimal(+rows[0].value.similarOnHand);
        let newSOH = rows
        .map(x => +x.value.count)
        .reduce((acc, count) => acc.sub(new Decimal(count)), actualSOH)
        .toNumber();

        rows.forEach(control => {
          const value = control.value;
          if (!!value.gpi) {
            control.get('availableSOH').setValue(newSOH);
          }
        });
      }
    }

  handleRowDeletedSideEffect(row) : void {
    this.backCountService.removeDrugIDRequiringBackCount(row.drugID);
    this.adjustSOH(row.gpi);
  }

  getLocationsToExclude(): ILocationToExclude[] {
   let alreadySelected = this.orderSalesReturnsFormArray.getRawValue()
   .map(x => ({id: x.locationID, drugID: x.drugID}));
    if (this.activityType === ActivityTypesEnum.SoldToProvider) {
      return [...alreadySelected, ...[{id: this.store.selectSnapshot(InventoryState.damagedExpiredLocationID)}]];
    }
    return alreadySelected;
  }

  saveFiles(event: ISaveAttachedFileEvent): void {
    const modifiedOrdNew = event?.attachments.map((x: IFileUploadObject) => ({...x, signature: event?.signature, createDate: x.isReplaced ? new Date(): x.createDate}));
    if (!event.isEditMode) {
      this.files = [...this.files, ...modifiedOrdNew];
    }
    else {
      this.files = [...modifiedOrdNew];
    }
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }


 submitForm(): void {
  if (this.form.invalid || this.orderSalesReturnsFormArray.invalid) {
    highlightRequiredControlsWithErrors(this.form);
    this.orderSalesReturnsFormArray.controls.forEach(element => {
      highlightRequiredControlsWithErrors(element);
    });
    this.supplierBuyerDetails.highlightRequiredControlsWithErrors();
  } else {
    this.emitFormData();
  }
 }

 private emitFormData(): void {
  const inputsData = this.form.getRawValue();
  const ndcsData = this.orderSalesReturnsFormArray.getRawValue();
  const payload = {
    transactionDate: inputsData.transactionDate,
    inventoryActions: ndcsData?.map(x => ({
      drugId: x.drugID, 
      addedQuantity: x.count, 
      activityType: ActivityTypesEnum.Dispensed,
      locationId : x.locationID
    }))
  }
  this.store.dispatch(new ValidateTransactionDateForInventories(payload))
  .pipe(untilDestroyed(this))
  .subscribe(resp => {
    const validateTrDateResponse = resp.inventory?.response;
    if (
      validateTrDateResponse?.success &&
      validateTrDateResponse?.value?.length
    ) {
      this.markConflictingNdcs(validateTrDateResponse.value?.map((x) => ({ drugId: x.drugId, locationId: x.locationId})));
      this.transactionsService.handleTransactionDateErrors(validateTrDateResponse.value);
    } else {
      this.submit.next({
        buyerDetailsID: inputsData.buyerDetailId,
        supplierDetailsID: inputsData.supplierDetailId,
        poNumber: inputsData.poNumber,
        mra: inputsData.mra,
        invoiceNumber: inputsData.invoiceNumber,
        csosNumber: inputsData.csosNumber,
        shipDate: inputsData.shipDate,
        transactionDate: inputsData.transactionDate,
        sellReturnNdcs: ndcsData.map((ndc) => ({
          drugId: ndc.drugID,
          locationId: ndc.locationID,
          addedQty: +ndc.count,
          note: ndc.note,
          isBackCountMatches: ndc.isBackCountMatches
        })),
        orderAttachments: this.files || []
      });
    }
  })
 }

 markConflictingNdcs(conflictingNDCs: IViolatingTransactionDateNdc[]): void {
  conflictingNDCs.forEach(ndc => {
    const drugControl = this.orderSalesReturnsFormArray.controls.find(control => control.value.drugID === ndc.drugId && control.value.locationID === ndc.locationId);
    drugControl?.get('count')?.setErrors({causedNegativeBalance: true});
  });
  this.form.get('transactionDateDisplayValue')?.setErrors({causedNegativeBalance: true});
  this.table?.markForCheck();
 }

  private initForm(): void {
    this.form = new FormGroup({
      buyerDetailId: new FormControl('', [Validators.required]),
      supplierDetailId: new FormControl(null),
      invoiceNumber: new FormControl(''),
      csosNumber: new FormControl('', [this.CSOSRequiredValidator.bind(this)]),
      shipDate: new FormControl('', [Validators.required]),
      transactionDate: new FormControl(new Date(), [Validators.required]),
      transactionDateDisplayValue: new FormControl(this.formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy')),
    });
    this.form.get('csosNumber').disable();
  }

  CSOSRequiredValidator(control: FormControl): any  {
    if ((this.orderSalesReturnsFormArray?.value)?.some(x => x?.schedule === DrugScheduleEnum.C2) && !control.value) {
      return { required: true };
    }
    return null;
  }


  now(): Date {
    return new Date();
  }


  handleLastInputTabbing(): void {
    if (this.form.invalid || this.orderSalesReturnsFormArray.invalid) {
      this.dialog.focusCancelButton();
    } else {
      this.dialog.focusSubmitButton();
    } 
  }

  datePickerFocusNext(): void {
    if (this.form.get('buyerDetailId').value && this.form.get('supplierDetailId').value) {
      setTimeout(() => {
        (<HTMLElement>document.querySelector(`app-dialog#${this.dialogId} tbody`)?.querySelectorAll('input')[0])?.focus();
      });
    } else {
      this.supplierBuyerDetails.focusInput();
    }
   
  }

  cancelAddNewNdc(): void {
    if (!this.orderSalesReturnsFormArray?.length) {
      this.cancel.emit();
    }
    this.addNdcMode = false;
  }

  setCurrentBuyerDetailId(id: string | null): void {
    this.form.get('buyerDetailId').patchValue(id);
  }

  setCurrentSupplierDetailId(id: string | null): void {
    this.form.get('supplierDetailId').patchValue(id);
  }

  ngOnDestroy(): void {
    this.files = null;
  }
}
